<template>
  <div class="p-3 sm:p-4 md:p-6 bg-gray-50 dark:bg-gray-900 min-h-screen">
    <!-- Header -->
    <div class="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3 mb-4">
      <div>
        <h1 class="text-xl sm:text-2xl font-bold text-gray-800 dark:text-white flex items-center gap-2">
          <Icon icon="mdi:office-building" class="text-blue-600 dark:text-blue-400" />
          Gestión de Empresas
        </h1>
        <p class="text-xs sm:text-sm text-gray-600 dark:text-gray-400">Administra las empresas o tenants del sistema</p>
      </div>
      <button
        @click="openCreateModal"
        class="w-full sm:w-auto flex items-center justify-center px-4 py-2.5 bg-gradient-to-r from-blue-600 to-indigo-600 text-white rounded-lg hover:from-blue-700 hover:to-indigo-700 transition-all shadow hover:shadow-md disabled:opacity-50 disabled:cursor-not-allowed text-sm sm:text-base"
        :disabled="!canCreate"
      >
        <Icon icon="mdi:plus" class="mr-2 text-lg" />
        Nueva Empresa
      </button>
    </div>

    <!-- Filtros -->
    <div class="bg-white dark:bg-gray-800 rounded-xl shadow-sm p-3 sm:p-4 mb-4 border border-gray-200 dark:border-gray-700">
      <div class="flex flex-col sm:flex-row gap-3 sm:gap-4">
        <div class="flex-1 w-full">
          <label class="block text-xs font-bold text-gray-400 uppercase mb-1">Buscar Empresa</label>
          <div class="relative group">
            <Icon icon="mdi:magnify" class="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 group-focus-within:text-blue-500 transition-colors" />
            <input 
              v-model="searchQuery" 
              type="text" 
              placeholder="Nombre o ID..." 
              class="w-full pl-10 pr-3 py-2 bg-gray-50 dark:bg-gray-700/50 border border-gray-200 dark:border-gray-600 rounded-lg text-sm focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all outline-none dark:text-white" 
            />
          </div>
        </div>
        <div class="w-full sm:w-auto">
          <label class="block text-xs font-bold text-gray-400 uppercase mb-1">Filtrar</label>
          <select 
            v-model="filtroEstado" 
            class="w-full sm:w-auto px-3 py-2 bg-gray-50 dark:bg-gray-700/50 border border-gray-200 dark:border-gray-600 rounded-lg text-sm focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all outline-none dark:text-white"
          >
            <option value="todos">📋 Todos</option>
            <option value="activo">✅ Activos</option>
            <option value="inactivo">❌ Inactivos</option>
          </select>
        </div>
      </div>
    </div>

    <!-- Contador -->
    <div v-if="!loading" class="text-xs text-gray-500 dark:text-gray-400 mb-2">
      Mostrando {{ filteredEmpresas.length }} de {{ empresas.length }} empresas
    </div>

    <!-- Tabla Desktop -->
    <div class="hidden md:block bg-white dark:bg-gray-800 rounded-xl shadow-md overflow-hidden border border-gray-200 dark:border-gray-700">
      <div class="overflow-x-auto">
        <table class="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
          <thead class="bg-gray-50 dark:bg-gray-700/50">
            <tr>
              <th class="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Empresa</th>
              <th class="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Base de Datos</th>
              <th class="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Creación</th>
              <th class="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Estado</th>
              <th class="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Tenant ID</th>
              <th class="px-4 py-3 text-center text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Acciones</th>
            </tr>
          </thead>
          <tbody class="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
            <tr v-if="loading">
              <td colspan="6" class="px-4 py-12 text-center text-gray-500">
                <Icon icon="mdi:loading" class="animate-spin text-3xl mx-auto mb-2" />
                Cargando empresas...
              </td>
            </tr>
            <tr v-else-if="filteredEmpresas.length === 0">
              <td colspan="6" class="px-4 py-12 text-center text-gray-500">
                <Icon icon="mdi:office-building-off" class="text-4xl mx-auto mb-2 text-gray-300" />
                No se encontraron empresas.
              </td>
            </tr>
            <tr v-else v-for="empresa in filteredEmpresas" :key="empresa.id" class="hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors">
              <td class="px-4 py-4 whitespace-nowrap">
                <div class="flex items-center">
                  <div class="h-10 w-10 rounded-full bg-gradient-to-br from-blue-100 to-indigo-100 dark:from-blue-900 dark:to-indigo-900 flex items-center justify-center mr-3 flex-shrink-0 relative">
                    <Icon icon="mdi:office-building" class="text-blue-600 dark:text-blue-300 text-lg" />
                    <!-- 👈 Badge empresa principal -->
                    <span v-if="esEmpresaPrincipal(empresa)" class="absolute -top-1 -right-1 w-4 h-4 bg-yellow-400 rounded-full flex items-center justify-center" title="Empresa principal del sistema">
                      <Icon icon="mdi:crown" class="text-yellow-800 text-[10px]" />
                    </span>
                  </div>
                  <div>
                    <div class="text-sm font-bold text-gray-900 dark:text-white truncate max-w-[150px]">
                      {{ empresa.nombre }}
                      <span v-if="esEmpresaPrincipal(empresa)" class="ml-1 text-[10px] font-normal text-yellow-600 dark:text-yellow-400">(Principal)</span>
                    </div>
                  </div>
                </div>
              </td>
              <td class="px-4 py-4 whitespace-nowrap">
                <span class="px-2 py-1 bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-300 rounded text-xs font-mono truncate max-w-[120px] inline-block">
                  {{ empresa.databaseName }}
                </span>
              </td>
              <td class="px-4 py-4 whitespace-nowrap text-sm text-gray-700 dark:text-gray-300">
                {{ formatDate(empresa.fechaCreacion) }}
              </td>
              <td class="px-4 py-4 whitespace-nowrap">
                <span :class="[
                  'px-3 py-1 inline-flex text-xs leading-5 font-semibold rounded-full',
                  empresa.activo ? 'bg-green-100 text-green-800 dark:bg-green-900/50 dark:text-green-300' : 'bg-red-100 text-red-800 dark:bg-red-900/50 dark:text-red-300'
                ]">
                  {{ empresa.activo ? '✅ Activo' : '❌ Inactivo' }}
                </span>
              </td>
              <td class="px-4 py-4 whitespace-nowrap">
                <span class="text-xs font-mono bg-gray-100 dark:bg-gray-700 px-2 py-1 rounded text-gray-600 dark:text-gray-400 truncate max-w-[100px] inline-block">
                  {{ empresa.id }}
                </span>
              </td>
              <td class="px-4 py-4 whitespace-nowrap text-sm">
                <div class="flex items-center justify-center gap-1">
                  <!-- 👈 NUEVO: Editar -->
                  <button 
                    @click="openEditModal(empresa)" 
                    class="p-2 bg-blue-50 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400 rounded-lg hover:bg-blue-100 dark:hover:bg-blue-900/50 transition-colors" 
                    title="Editar configuración (logo, info)"
                  >
                    <Icon icon="mdi:pencil" class="text-lg" />
                  </button>

                  <!-- 👈 PROTEGIDO: no se puede desactivar empresa principal -->
                  <button 
                    v-if="!esEmpresaPrincipal(empresa)"
                    @click="toggleStatus(empresa)" 
                    class="p-2 bg-amber-50 dark:bg-amber-900/30 text-amber-600 dark:text-amber-400 rounded-lg hover:bg-amber-100 dark:hover:bg-amber-900/50 transition-colors" 
                    :title="empresa.activo ? 'Desactivar' : 'Activar'"
                  >
                    <Icon :icon="empresa.activo ? 'mdi:eye-off' : 'mdi:eye'" class="text-lg" />
                  </button>
                  <button 
                    v-else
                    disabled
                    class="p-2 bg-gray-100 dark:bg-gray-700/50 text-gray-300 dark:text-gray-600 rounded-lg cursor-not-allowed" 
                    title="La empresa principal no se puede desactivar"
                  >
                    <Icon icon="mdi:lock" class="text-lg" />
                  </button>

                  <button 
                    v-if="isSuperAdmin" 
                    @click="confirmarEliminarConfig(empresa)" 
                    class="p-2 bg-orange-50 dark:bg-orange-900/30 text-orange-600 dark:text-orange-400 rounded-lg hover:bg-orange-100 dark:hover:bg-orange-900/50 transition-colors" 
                    title="Eliminar configuración visual (logo + info)"
                  >
                    <Icon icon="mdi:image-off" class="text-lg" />
                  </button>

                  <!-- 👈 PROTEGIDO: no se puede eliminar empresa principal -->
                  <button 
                    v-if="isSuperAdmin && !esEmpresaPrincipal(empresa)" 
                    @click="confirmDelete(empresa)" 
                    class="p-2 bg-red-50 dark:bg-red-900/30 text-red-600 dark:text-red-400 rounded-lg hover:bg-red-100 dark:hover:bg-red-900/50 transition-colors" 
                    title="Eliminar"
                  >
                    <Icon icon="mdi:delete" class="text-lg" />
                  </button>
                  <button 
                    v-else-if="isSuperAdmin"
                    disabled
                    class="p-2 bg-gray-100 dark:bg-gray-700/50 text-gray-300 dark:text-gray-600 rounded-lg cursor-not-allowed" 
                    title="La empresa principal no se puede eliminar"
                  >
                    <Icon icon="mdi:lock" class="text-lg" />
                  </button>
                </div>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>

    <!-- Tarjetas Móvil -->
    <div class="md:hidden space-y-3">
      <div v-if="loading" class="text-center py-12">
        <Icon icon="mdi:loading" class="animate-spin text-3xl mx-auto mb-2 text-gray-500" />
        <p class="text-gray-500">Cargando empresas...</p>
      </div>
      <div v-else-if="filteredEmpresas.length === 0" class="text-center py-12">
        <Icon icon="mdi:office-building-off" class="text-4xl mx-auto mb-2 text-gray-300" />
        <p class="text-gray-500">No se encontraron empresas.</p>
      </div>
      <div 
        v-else 
        v-for="empresa in filteredEmpresas" 
        :key="empresa.id" 
        class="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-4 hover:shadow-md transition-shadow"
      >
        <div class="flex items-start justify-between">
          <div class="flex items-start gap-3 flex-1 min-w-0">
            <div class="h-12 w-12 rounded-full bg-gradient-to-br from-blue-100 to-indigo-100 dark:from-blue-900 dark:to-indigo-900 flex items-center justify-center flex-shrink-0 relative">
              <Icon icon="mdi:office-building" class="text-blue-600 dark:text-blue-300 text-2xl" />
              <span v-if="esEmpresaPrincipal(empresa)" class="absolute -top-1 -right-1 w-5 h-5 bg-yellow-400 rounded-full flex items-center justify-center" title="Empresa principal">
                <Icon icon="mdi:crown" class="text-yellow-800 text-xs" />
              </span>
            </div>
            <div class="flex-1 min-w-0">
              <h3 class="text-base font-bold text-gray-900 dark:text-white truncate">
                {{ empresa.nombre }}
                <span v-if="esEmpresaPrincipal(empresa)" class="ml-1 text-[10px] font-normal text-yellow-600 dark:text-yellow-400">(Principal)</span>
              </h3>
              <div class="flex items-center gap-2 mt-1 flex-wrap">
                <span :class="[
                  'px-2 py-0.5 text-xs font-semibold rounded-full',
                  empresa.activo ? 'bg-green-100 text-green-800 dark:bg-green-900/50 dark:text-green-300' : 'bg-red-100 text-red-800 dark:bg-red-900/50 dark:text-red-300'
                ]">
                  {{ empresa.activo ? 'Activo' : 'Inactivo' }}
                </span>
                <span class="text-xs text-gray-500 dark:text-gray-400 font-mono bg-gray-100 dark:bg-gray-700 px-2 py-0.5 rounded">
                  {{ empresa.id }}
                </span>
              </div>
              <div class="mt-2 text-xs text-gray-600 dark:text-gray-400 space-y-0.5">
                <div class="flex items-center gap-1">
                  <Icon icon="mdi:database" class="text-purple-500 text-sm" />
                  <span class="font-mono truncate">{{ empresa.databaseName }}</span>
                </div>
                <div class="flex items-center gap-1">
                  <Icon icon="mdi:calendar" class="text-gray-400 text-sm" />
                  <span>{{ formatDate(empresa.fechaCreacion) }}</span>
                </div>
              </div>
            </div>
          </div>
          <div class="flex flex-col gap-1 ml-2 flex-shrink-0">
            <button 
              @click="openEditModal(empresa)" 
              class="p-2 bg-blue-50 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400 rounded-lg hover:bg-blue-100 dark:hover:bg-blue-900/50 transition-colors" 
              title="Editar"
            >
              <Icon icon="mdi:pencil" class="text-lg" />
            </button>
            <button 
              v-if="!esEmpresaPrincipal(empresa)"
              @click="toggleStatus(empresa)" 
              class="p-2 bg-amber-50 dark:bg-amber-900/30 text-amber-600 dark:text-amber-400 rounded-lg hover:bg-amber-100 dark:hover:bg-amber-900/50 transition-colors" 
              :title="empresa.activo ? 'Desactivar' : 'Activar'"
            >
              <Icon :icon="empresa.activo ? 'mdi:eye-off' : 'mdi:eye'" class="text-lg" />
            </button>
            <button 
              v-else
              disabled
              class="p-2 bg-gray-100 dark:bg-gray-700/50 text-gray-300 dark:text-gray-600 rounded-lg cursor-not-allowed" 
              title="La empresa principal no se puede desactivar"
            >
              <Icon icon="mdi:lock" class="text-lg" />
            </button>
            <button 
              v-if="isSuperAdmin" 
              @click="confirmarEliminarConfig(empresa)" 
              class="p-2 bg-orange-50 dark:bg-orange-900/30 text-orange-600 dark:text-orange-400 rounded-lg hover:bg-orange-100 dark:hover:bg-orange-900/50 transition-colors" 
              title="Eliminar configuración visual"
            >
              <Icon icon="mdi:image-off" class="text-lg" />
            </button>
            <button 
              v-if="isSuperAdmin && !esEmpresaPrincipal(empresa)" 
              @click="confirmDelete(empresa)" 
              class="p-2 bg-red-50 dark:bg-red-900/30 text-red-600 dark:text-red-400 rounded-lg hover:bg-red-100 dark:hover:bg-red-900/50 transition-colors" 
              title="Eliminar"
            >
              <Icon icon="mdi:delete" class="text-lg" />
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- ============================================================ -->
    <!-- MODAL CREAR / EDITAR EMPRESA                                -->
    <!-- ============================================================ -->
    <div v-if="showModal" class="fixed inset-0 z-50 overflow-y-auto flex items-center justify-center bg-black/60 backdrop-blur-sm p-2 sm:p-4">
      <div class="bg-white dark:bg-gray-800 rounded-2xl w-full max-w-3xl shadow-2xl animate-fadeIn mx-auto my-4 max-h-[95vh] flex flex-col overflow-hidden">

        <!-- Header con gradiente -->
        <div :class="[
          'relative px-5 sm:px-6 py-4 sm:py-5 flex items-center justify-between flex-shrink-0',
          isEditing 
            ? 'bg-gradient-to-r from-amber-500 via-orange-500 to-red-500' 
            : 'bg-gradient-to-r from-blue-600 via-indigo-600 to-purple-600'
        ]">
          <div class="flex items-center gap-3">
            <div class="w-12 h-12 rounded-2xl bg-white/20 backdrop-blur-md flex items-center justify-center">
              <Icon :icon="isEditing ? 'mdi:pencil-circle' : 'mdi:office-building-plus'" class="text-white text-2xl" />
            </div>
            <div>
              <h3 class="text-lg sm:text-xl font-black text-white tracking-tight">
                {{ isEditing ? 'Editar Empresa' : 'Nueva Empresa' }}
              </h3>
              <p class="text-[10px] sm:text-xs text-white/80 font-medium">
                {{ isEditing ? 'Modifica la configuración visual y comercial' : 'Configura la empresa, su sede y su técnico principal' }}
              </p>
            </div>
          </div>
          <button @click="closeModal" class="w-9 h-9 rounded-xl bg-white/10 hover:bg-white/20 flex items-center justify-center text-white transition-all">
            <Icon icon="mdi:close" class="text-xl" />
          </button>
        </div>

        <!-- Stepper / Indicadores -->
        <div class="flex items-center justify-center gap-2 sm:gap-4 px-4 py-3 bg-gray-50 dark:bg-gray-900/50 border-b border-gray-200 dark:border-gray-700 flex-shrink-0 overflow-x-auto custom-scrollbar">
          <div
            v-for="(step, i) in stepsFiltrados"
            :key="step.id"
            @click="activeStep = step.id"
            class="flex items-center gap-2 cursor-pointer flex-shrink-0 group"
          >
            <div :class="[
              'w-7 h-7 rounded-full flex items-center justify-center text-xs font-black transition-all',
              activeStep === step.id
                ? (isEditing ? 'bg-amber-500 text-white shadow-lg shadow-amber-500/30 scale-110' : 'bg-indigo-600 text-white shadow-lg shadow-indigo-500/30 scale-110')
                : 'bg-gray-200 dark:bg-gray-700 text-gray-500 dark:text-gray-400 group-hover:bg-gray-300 dark:group-hover:bg-gray-600'
            ]">
              {{ i + 1 }}
            </div>
            <span :class="[
              'text-xs font-bold uppercase tracking-wider transition-colors',
              activeStep === step.id 
                ? (isEditing ? 'text-amber-600 dark:text-amber-400' : 'text-indigo-600 dark:text-indigo-400')
                : 'text-gray-400 dark:text-gray-500'
            ]">{{ step.name }}</span>
            <Icon v-if="i < stepsFiltrados.length - 1" icon="mdi:chevron-right" class="text-gray-300 dark:text-gray-600 text-sm" />
          </div>
        </div>

        <!-- Contenido scrollable -->
        <form @submit.prevent="saveForm" class="flex-1 overflow-y-auto custom-scrollbar">
          <div class="p-5 sm:p-6 space-y-6">

            <!-- PASO 1: EMPRESA -->
            <div v-show="activeStep === 'empresa'" class="space-y-4 animate-fadeIn">
              <!-- Nombre -->
              <div>
                <label class="block text-sm font-bold text-gray-700 dark:text-gray-300 mb-2 flex items-center gap-2">
                  <Icon icon="mdi:office-building" class="text-indigo-500" />
                  Nombre de la Empresa <span class="text-red-500">*</span>
                </label>
                <input 
                  v-model="form.nombreEmpresa" 
                  type="text" 
                  required 
                  :readonly="isEditing"
                  :class="[
                    'w-full px-4 py-3 border-2 rounded-xl text-sm font-medium outline-none dark:text-white transition-all',
                    isEditing 
                      ? 'bg-gray-100 dark:bg-gray-900/80 border-gray-200 dark:border-gray-700 text-gray-500 cursor-not-allowed'
                      : 'bg-gray-50 dark:bg-gray-900/50 border-gray-200 dark:border-gray-700 focus:border-indigo-500'
                  ]"
                  placeholder="Ej: Mi Empresa C.A." 
                  @input="!isEditing && generarTenantId()"
                  @blur="!isEditing && (form.nombreEmpresa = toTitleCase(form.nombreEmpresa))"
                  maxlength="100"
                  ref="nombreInput"
                />
                <p v-if="isEditing" class="text-[10px] text-gray-400 mt-1 flex items-center gap-1">
                  <Icon icon="mdi:lock" />
                  El nombre no se puede modificar una vez creada la empresa
                </p>
              </div>

              <!-- Tenant ID -->
              <div>
                <label class="block text-sm font-bold text-gray-700 dark:text-gray-300 mb-2 flex items-center gap-2">
                  <Icon icon="mdi:identifier" class="text-purple-500" />
                  Tenant ID <span class="text-red-500">*</span>
                  <span v-if="!isEditing" class="text-xs font-normal text-gray-400 ml-auto">(auto-generado)</span>
                  <span v-else class="text-xs font-normal text-gray-400 ml-auto">(no editable)</span>
                </label>
                <div class="flex gap-2">
                  <input 
                    v-model="form.tenantId" 
                    type="text" 
                    required 
                    readonly
                    class="flex-1 px-4 py-3 bg-gray-100 dark:bg-gray-900/80 border-2 border-gray-200 dark:border-gray-700 rounded-xl text-sm font-mono text-gray-600 dark:text-gray-400 cursor-not-allowed"
                  />
                  <button v-if="!isEditing" type="button" @click="regenerarTenantId" class="px-3 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 rounded-xl transition-all" title="Regenerar">
                    <Icon icon="mdi:refresh" class="text-gray-600 dark:text-gray-300" />
                  </button>
                  <button type="button" @click="copiarTenantId" class="px-3 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 rounded-xl transition-all" title="Copiar">
                    <Icon icon="mdi:content-copy" class="text-gray-600 dark:text-gray-300" />
                  </button>
                </div>
              </div>

              <!-- Logo y Favicon (lado a lado) -->
              <div class="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <!-- LOGO -->
                <div>
                  <label class="block text-sm font-bold text-gray-700 dark:text-gray-300 mb-2 flex items-center gap-2">
                    <Icon icon="mdi:image" class="text-blue-500" />
                    Logo
                    <span class="text-[10px] font-normal text-gray-400">(opcional)</span>
                  </label>
                  <div class="relative">
                    <div v-if="logoPreview" class="relative group">
                      <img :src="logoPreview" class="w-full h-32 object-contain rounded-xl border-2 border-gray-200 dark:border-gray-700 bg-white" />
                      <button type="button" @click="quitarLogo" class="absolute top-2 right-2 bg-red-500 hover:bg-red-600 text-white rounded-full w-7 h-7 flex items-center justify-center shadow-lg transition-all">
                        <Icon icon="mdi:close" class="text-sm" />
                      </button>
                    </div>
                    <label v-else class="flex flex-col items-center justify-center h-32 border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-xl cursor-pointer hover:border-indigo-500 hover:bg-indigo-50 dark:hover:bg-indigo-900/20 transition-all group">
                      <div class="w-12 h-12 rounded-full bg-indigo-100 dark:bg-indigo-900/30 flex items-center justify-center mb-2 group-hover:scale-110 transition-transform">
                        <Icon icon="mdi:image-plus" class="text-indigo-600 dark:text-indigo-400 text-2xl" />
                      </div>
                      <span class="text-xs font-bold text-gray-500 dark:text-gray-400">Subir logo</span>
                      <span class="text-[10px] text-gray-400 mt-0.5">PNG, JPG · Máx 2MB</span>
                      <input type="file" accept="image/*" @change="onLogoSelected" class="hidden" />
                    </label>
                  </div>
                </div>

                <!-- FAVICON -->
                <div>
                  <label class="block text-sm font-bold text-gray-700 dark:text-gray-300 mb-2 flex items-center gap-2">
                    <Icon icon="mdi:star" class="text-yellow-500" />
                    Favicon
                    <span class="text-[10px] font-normal text-gray-400">(opcional)</span>
                  </label>
                  <div class="relative">
                    <div v-if="faviconPreview" class="relative group">
                      <img :src="faviconPreview" class="w-full h-32 object-contain rounded-xl border-2 border-gray-200 dark:border-gray-700 bg-white" />
                      <button type="button" @click="quitarFavicon" class="absolute top-2 right-2 bg-red-500 hover:bg-red-600 text-white rounded-full w-7 h-7 flex items-center justify-center shadow-lg transition-all">
                        <Icon icon="mdi:close" class="text-sm" />
                      </button>
                    </div>
                    <label v-else class="flex flex-col items-center justify-center h-32 border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-xl cursor-pointer hover:border-yellow-500 hover:bg-yellow-50 dark:hover:bg-yellow-900/20 transition-all group">
                      <div class="w-12 h-12 rounded-full bg-yellow-100 dark:bg-yellow-900/30 flex items-center justify-center mb-2 group-hover:scale-110 transition-transform">
                        <Icon icon="mdi:star-plus" class="text-yellow-600 dark:text-yellow-400 text-2xl" />
                      </div>
                      <span class="text-xs font-bold text-gray-500 dark:text-gray-400">Subir favicon</span>
                      <span class="text-[10px] text-gray-400 mt-0.5">32x32 · Máx 500KB</span>
                      <input type="file" accept="image/*" @change="onFaviconSelected" class="hidden" />
                    </label>
                  </div>
                </div>
              </div>
            </div>

            <!-- PASO 2: INFORMACIÓN COMERCIAL -->
            <div v-show="activeStep === 'info'" class="space-y-4 animate-fadeIn">
              <div class="bg-blue-50 dark:bg-blue-900/10 border border-blue-200 dark:border-blue-800 rounded-xl p-3 flex items-start gap-2">
                <Icon icon="mdi:information" class="text-blue-500 flex-shrink-0 mt-0.5" />
                <p class="text-xs text-blue-700 dark:text-blue-300">
                  Esta información aparecerá en las facturas y tickets de la empresa.
                </p>
              </div>

              <div>
                <label class="block text-sm font-bold text-gray-700 dark:text-gray-300 mb-2">Nombre Comercial</label>
                <input v-model="formConfig.nombreComercial" type="text" :placeholder="form.nombreEmpresa || 'Ej: Mi Empresa'" class="w-full px-4 py-3 bg-gray-50 dark:bg-gray-900/50 border-2 border-gray-200 dark:border-gray-700 focus:border-indigo-500 rounded-xl text-sm outline-none dark:text-white transition-all" maxlength="100" />
              </div>

              <div class="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label class="block text-sm font-bold text-gray-700 dark:text-gray-300 mb-2">RIF</label>
                  <input v-model="formConfig.rif" type="text" placeholder="J-12345678-9" class="w-full px-4 py-3 bg-gray-50 dark:bg-gray-900/50 border-2 border-gray-200 dark:border-gray-700 focus:border-indigo-500 rounded-xl text-sm outline-none dark:text-white transition-all" maxlength="20" />
                </div>
                <div>
                  <label class="block text-sm font-bold text-gray-700 dark:text-gray-300 mb-2">Ciudad</label>
                  <input v-model="formConfig.ciudad" type="text" placeholder="Ej: Acarigua - Portuguesa" class="w-full px-4 py-3 bg-gray-50 dark:bg-gray-900/50 border-2 border-gray-200 dark:border-gray-700 focus:border-indigo-500 rounded-xl text-sm outline-none dark:text-white transition-all" maxlength="100" />
                </div>
              </div>

              <div>
                <label class="block text-sm font-bold text-gray-700 dark:text-gray-300 mb-2">Dirección</label>
                <input v-model="formConfig.direccion" type="text" placeholder="Ej: C.C. Latin Center PB Local 01" class="w-full px-4 py-3 bg-gray-50 dark:bg-gray-900/50 border-2 border-gray-200 dark:border-gray-700 focus:border-indigo-500 rounded-xl text-sm outline-none dark:text-white transition-all" maxlength="150" />
              </div>

              <div class="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label class="block text-sm font-bold text-gray-700 dark:text-gray-300 mb-2">Teléfono 1</label>
                  <input v-model="formConfig.telefono1" type="text" placeholder="0412-1234567" class="w-full px-4 py-3 bg-gray-50 dark:bg-gray-900/50 border-2 border-gray-200 dark:border-gray-700 focus:border-indigo-500 rounded-xl text-sm outline-none dark:text-white transition-all" maxlength="20" />
                </div>
                <div>
                  <label class="block text-sm font-bold text-gray-700 dark:text-gray-300 mb-2">Teléfono 2</label>
                  <input v-model="formConfig.telefono2" type="text" placeholder="0416-7654321" class="w-full px-4 py-3 bg-gray-50 dark:bg-gray-900/50 border-2 border-gray-200 dark:border-gray-700 focus:border-indigo-500 rounded-xl text-sm outline-none dark:text-white transition-all" maxlength="20" />
                </div>
              </div>

              <div>
                <label class="block text-sm font-bold text-gray-700 dark:text-gray-300 mb-2">Email</label>
                <input v-model="formConfig.email" type="email" placeholder="correo@empresa.com" class="w-full px-4 py-3 bg-gray-50 dark:bg-gray-900/50 border-2 border-gray-200 dark:border-gray-700 focus:border-indigo-500 rounded-xl text-sm outline-none dark:text-white transition-all" maxlength="100" />
              </div>

              <div class="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label class="block text-sm font-bold text-gray-700 dark:text-gray-300 mb-2">Slogan</label>
                  <input v-model="formConfig.slogan" type="text" placeholder="Ej: Servicio Técnico Especializado" class="w-full px-4 py-3 bg-gray-50 dark:bg-gray-900/50 border-2 border-gray-200 dark:border-gray-700 focus:border-indigo-500 rounded-xl text-sm outline-none dark:text-white transition-all" maxlength="150" />
                </div>
                <div>
                  <label class="block text-sm font-bold text-gray-700 dark:text-gray-300 mb-2">WhatsApp</label>
                  <input v-model="formConfig.whatsapp" type="text" placeholder="0412-1234567" class="w-full px-4 py-3 bg-gray-50 dark:bg-gray-900/50 border-2 border-gray-200 dark:border-gray-700 focus:border-indigo-500 rounded-xl text-sm outline-none dark:text-white transition-all" maxlength="20" />
                </div>
              </div>

              <div>
                <label class="block text-sm font-bold text-gray-700 dark:text-gray-300 mb-2">Instagram</label>
                <input v-model="formConfig.instagram" type="text" placeholder="@miempresa" class="w-full px-4 py-3 bg-gray-50 dark:bg-gray-900/50 border-2 border-gray-200 dark:border-gray-700 focus:border-indigo-500 rounded-xl text-sm outline-none dark:text-white transition-all" maxlength="50" />
              </div>
            </div>

            <!-- PASO 3: SEDE (solo creación) -->
            <div v-show="activeStep === 'sede' && !isEditing" class="space-y-4 animate-fadeIn">
              <div class="bg-emerald-50 dark:bg-emerald-900/10 border border-emerald-200 dark:border-emerald-800 rounded-xl p-3 flex items-start gap-2">
                <Icon icon="mdi:store-marker" class="text-emerald-500 flex-shrink-0 mt-0.5" />
                <p class="text-xs text-emerald-700 dark:text-emerald-300">
                  Se creará una sede principal para esta empresa.
                </p>
              </div>

              <div>
                <label class="block text-sm font-bold text-gray-700 dark:text-gray-300 mb-2 flex items-center gap-2">
                  <Icon icon="mdi:store" class="text-emerald-500" />
                  Nombre de la Sede <span class="text-red-500">*</span>
                </label>
                <input v-model="formSede.nombre" type="text" placeholder="Ej: Sede Principal" class="w-full px-4 py-3 bg-gray-50 dark:bg-gray-900/50 border-2 border-gray-200 dark:border-gray-700 focus:border-emerald-500 rounded-xl text-sm outline-none dark:text-white transition-all" maxlength="100" />
              </div>

              <div>
                <label class="block text-sm font-bold text-gray-700 dark:text-gray-300 mb-2">Dirección de la Sede</label>
                <input v-model="formSede.direccion" type="text" placeholder="Ej: Av. Principal, Local 1" class="w-full px-4 py-3 bg-gray-50 dark:bg-gray-900/50 border-2 border-gray-200 dark:border-gray-700 focus:border-emerald-500 rounded-xl text-sm outline-none dark:text-white transition-all" maxlength="200" />
              </div>

              <div class="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label class="block text-sm font-bold text-gray-700 dark:text-gray-300 mb-2">Teléfono Sede</label>
                  <input v-model="formSede.telefono" type="text" placeholder="0412-1234567" class="w-full px-4 py-3 bg-gray-50 dark:bg-gray-900/50 border-2 border-gray-200 dark:border-gray-700 focus:border-emerald-500 rounded-xl text-sm outline-none dark:text-white transition-all" maxlength="20" />
                </div>
                <div>
                  <label class="block text-sm font-bold text-gray-700 dark:text-gray-300 mb-2">Email Sede</label>
                  <input v-model="formSede.email" type="email" placeholder="sede@empresa.com" class="w-full px-4 py-3 bg-gray-50 dark:bg-gray-900/50 border-2 border-gray-200 dark:border-gray-700 focus:border-emerald-500 rounded-xl text-sm outline-none dark:text-white transition-all" maxlength="100" />
                </div>
              </div>

              <div>
                <label class="block text-sm font-bold text-gray-700 dark:text-gray-300 mb-2">Ciudad Sede</label>
                <input v-model="formSede.ciudad" type="text" placeholder="Ej: Acarigua - Portuguesa" class="w-full px-4 py-3 bg-gray-50 dark:bg-gray-900/50 border-2 border-gray-200 dark:border-gray-700 focus:border-emerald-500 rounded-xl text-sm outline-none dark:text-white transition-all" maxlength="100" />
              </div>
            </div>

            <!-- PASO 4: TÉCNICO (solo creación) -->
            <div v-show="activeStep === 'tecnico' && !isEditing" class="space-y-4 animate-fadeIn">
              <div class="bg-purple-50 dark:bg-purple-900/10 border border-purple-200 dark:border-purple-800 rounded-xl p-3 flex items-start gap-2">
                <Icon icon="mdi:account-wrench" class="text-purple-500 flex-shrink-0 mt-0.5" />
                <p class="text-xs text-purple-700 dark:text-purple-300">
                  Se creará un técnico principal para esta empresa.
                </p>
              </div>

              <div class="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label class="block text-sm font-bold text-gray-700 dark:text-gray-300 mb-2 flex items-center gap-2">
                    <Icon icon="mdi:account" class="text-purple-500" />
                    Nombre <span class="text-red-500">*</span>
                  </label>
                  <input v-model="formTecnico.nombre" type="text" placeholder="Ej: Juan" class="w-full px-4 py-3 bg-gray-50 dark:bg-gray-900/50 border-2 border-gray-200 dark:border-gray-700 focus:border-purple-500 rounded-xl text-sm outline-none dark:text-white transition-all" maxlength="100" />
                </div>
                <div>
                  <label class="block text-sm font-bold text-gray-700 dark:text-gray-300 mb-2">Apellido</label>
                  <input v-model="formTecnico.apellido" type="text" placeholder="Ej: Pérez" class="w-full px-4 py-3 bg-gray-50 dark:bg-gray-900/50 border-2 border-gray-200 dark:border-gray-700 focus:border-purple-500 rounded-xl text-sm outline-none dark:text-white transition-all" maxlength="100" />
                </div>
              </div>

              <div>
                <label class="block text-sm font-bold text-gray-700 dark:text-gray-300 mb-2">Especialidad</label>
                <input v-model="formTecnico.especialidad" type="text" placeholder="Ej: Reparación de computadoras" class="w-full px-4 py-3 bg-gray-50 dark:bg-gray-900/50 border-2 border-gray-200 dark:border-gray-700 focus:border-purple-500 rounded-xl text-sm outline-none dark:text-white transition-all" maxlength="100" />
              </div>

              <div class="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label class="block text-sm font-bold text-gray-700 dark:text-gray-300 mb-2">Teléfono</label>
                  <input v-model="formTecnico.telefono" type="text" placeholder="0412-1234567" class="w-full px-4 py-3 bg-gray-50 dark:bg-gray-900/50 border-2 border-gray-200 dark:border-gray-700 focus:border-purple-500 rounded-xl text-sm outline-none dark:text-white transition-all" maxlength="20" />
                </div>
                <div>
                  <label class="block text-sm font-bold text-gray-700 dark:text-gray-300 mb-2">Comisión (%)</label>
                  <input v-model.number="formTecnico.comisionPorcentaje" type="number" min="0" max="100" step="0.01" placeholder="Ej: 10" class="w-full px-4 py-3 bg-gray-50 dark:bg-gray-900/50 border-2 border-gray-200 dark:border-gray-700 focus:border-purple-500 rounded-xl text-sm outline-none dark:text-white transition-all" />
                </div>
              </div>

              <div>
                <label class="block text-sm font-bold text-gray-700 dark:text-gray-300 mb-2">Descripción</label>
                <textarea v-model="formTecnico.descripcion" rows="3" placeholder="Descripción breve del técnico..." class="w-full px-4 py-3 bg-gray-50 dark:bg-gray-900/50 border-2 border-gray-200 dark:border-gray-700 focus:border-purple-500 rounded-xl text-sm outline-none dark:text-white transition-all resize-none" maxlength="500"></textarea>
              </div>
            </div>

          </div>

          <!-- Footer con botones -->
          <div class="px-5 sm:px-6 py-4 bg-gray-50 dark:bg-gray-900/50 border-t border-gray-200 dark:border-gray-700 flex flex-col sm:flex-row justify-between items-center gap-3 sticky bottom-0">
            <button
              type="button"
              @click="prevStep"
              :disabled="activeStep === stepsFiltrados[0].id"
              class="w-full sm:w-auto px-4 py-2.5 text-sm font-bold text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-xl transition-all disabled:opacity-40 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            >
              <Icon icon="mdi:chevron-left" />
              Anterior
            </button>

            <div class="flex flex-col sm:flex-row gap-2 w-full sm:w-auto">
              <button
                type="button"
                @click="closeModal"
                class="w-full sm:w-auto px-4 py-2.5 text-sm font-bold text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-xl transition-all"
              >
                Cancelar
              </button>

              <button
                v-if="activeStep !== stepsFiltrados[stepsFiltrados.length - 1].id"
                type="button"
                @click="nextStep"
                :class="[
                  'w-full sm:w-auto px-6 py-2.5 text-white rounded-xl text-sm font-bold transition-all shadow-lg flex items-center justify-center gap-2',
                  isEditing 
                    ? 'bg-amber-500 hover:bg-amber-600 shadow-amber-500/20' 
                    : 'bg-indigo-600 hover:bg-indigo-700 shadow-indigo-500/20'
                ]"
              >
                Siguiente
                <Icon icon="mdi:chevron-right" />
              </button>

              <button
                v-else
                type="submit"
                :disabled="saving || !form.tenantId || !form.nombreEmpresa"
                :class="[
                  'w-full sm:w-auto px-6 py-2.5 text-white rounded-xl text-sm font-black uppercase tracking-wider transition-all shadow-lg disabled:opacity-50 flex items-center justify-center gap-2',
                  isEditing 
                    ? 'bg-gradient-to-r from-amber-500 to-orange-600 hover:from-amber-600 hover:to-orange-700 shadow-amber-500/20' 
                    : 'bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-700 hover:to-teal-700 shadow-emerald-500/20'
                ]"
              >
                <Icon :icon="saving ? 'mdi:loading' : (isEditing ? 'mdi:content-save' : 'mdi:check-bold')" :class="{'animate-spin': saving}" />
                {{ saving ? 'Guardando...' : (isEditing ? 'Guardar Cambios' : 'Crear Empresa') }}
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>

    <!-- Modal Confirmación Eliminar -->
    <div v-if="showDeleteModal" class="fixed inset-0 z-50 overflow-y-auto flex items-center justify-center bg-black/60 backdrop-blur-sm p-4">
      <div class="bg-white dark:bg-gray-800 rounded-2xl w-full max-w-md shadow-2xl animate-fadeIn mx-auto">
        <div class="px-4 sm:px-6 py-4 border-b border-gray-200 dark:border-gray-700 flex justify-between items-center">
          <h3 class="text-lg sm:text-xl font-bold text-red-600 dark:text-red-400 flex items-center gap-2">
            <Icon icon="mdi:alert-circle" /> Confirmar Eliminación
          </h3>
          <button @click="closeDeleteModal" class="text-gray-500 hover:text-gray-700 dark:hover:text-gray-300 p-1">
            <Icon icon="mdi:close" class="text-2xl" />
          </button>
        </div>
        <div class="p-4 sm:p-6">
          <div class="flex items-start gap-3 mb-4">
            <Icon icon="mdi:alert-octagon" class="text-red-500 text-3xl flex-shrink-0 mt-1" />
            <div>
              <p class="text-gray-700 dark:text-gray-300 font-medium">¿Estás seguro de eliminar la empresa?</p>
              <p class="text-sm font-bold text-gray-900 dark:text-white mt-1">"{{ empresaAEliminar?.nombre }}"</p>
              <p class="text-xs text-gray-500 dark:text-gray-400 mt-2">
                <Icon icon="mdi:identifier" class="inline mr-1" /> ID: {{ empresaAEliminar?.id }}
              </p>
              <p class="text-sm text-red-500 mt-2 flex items-center gap-1">
                <Icon icon="mdi:alert" /> Esta acción no se puede deshacer.
              </p>
            </div>
          </div>
          <div class="flex flex-col sm:flex-row justify-end gap-3 pt-2 border-t border-gray-200 dark:border-gray-700">
            <button @click="closeDeleteModal" class="w-full sm:w-auto px-4 py-2.5 border rounded-lg text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 transition text-sm font-medium">Cancelar</button>
            <button @click="deleteEmpresa" :disabled="deleting" class="w-full sm:w-auto px-4 py-2.5 bg-red-600 hover:bg-red-700 text-white rounded-lg transition disabled:opacity-50 text-sm font-medium">
              <span v-if="deleting"><Icon icon="mdi:loading" class="animate-spin inline mr-2" />Eliminando...</span>
              <span v-else><Icon icon="mdi:delete" class="inline mr-2" />Sí, Eliminar</span>
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Toast -->
    <div v-if="toastMessage" class="fixed bottom-4 right-4 z-50 animate-slideUp">
      <div :class="['px-4 py-3 rounded-lg shadow-lg flex items-center gap-3 text-white', toastType === 'success' ? 'bg-green-600' : 'bg-red-600']">
        <Icon :icon="toastType === 'success' ? 'mdi:check-circle' : 'mdi:alert-circle'" class="text-xl" />
        <span>{{ toastMessage }}</span>
        <button @click="toastMessage = ''" class="ml-2 hover:opacity-75"><Icon icon="mdi:close" /></button>
      </div>
    </div>
  </div>
</template>

<script>
import { Icon } from "@iconify/vue";
import { 
  obtenerEmpresas, 
  crearEmpresa, 
  cambiarEstadoEmpresa, 
  eliminarEmpresa,
  obtenerConfigEmpresa
} from "@/api/empresa.js";
import { getCurrentUser } from "@/utils/permissionUtils";
import { 
  guardarConfigTenantSinAplicar,   // 👈 para crear (no aplica al DOM)
  guardarConfigTenant,              // 👈 para editar (solo si es mi empresa)
  obtenerConfigTenant,              // 👈 para comparar
  eliminarConfigTenant, 
  archivoABase64 
} from "@/utils/tenantConfig";
import { crearSede } from "@/api/sede.js";
import { crearTecnico } from "@/api/tecnicos.js";

// 👈 Nombre de la base de datos principal (no se puede eliminar/desactivar)
const DB_PRINCIPAL = 'jeaninformatico_db';
const TENANT_PRINCIPAL = 'default';

export default {
  name: 'EmpresaList',
  components: { Icon },
  data() {
    return {
      empresas: [],
      loading: false,
      saving: false,
      deleting: false,
      searchQuery: '',
      filtroEstado: 'todos',
      showModal: false,
      showDeleteModal: false,
      empresaAEliminar: null,
      toastMessage: '',
      toastType: 'success',
      toastTimeout: null,

      // 👈 modo edición
      isEditing: false,
      empresaEditando: null,

      // PASOS del stepper
      steps: [
        { id: 'empresa', name: 'Empresa', icon: 'mdi:office-building' },
        { id: 'info', name: 'Info', icon: 'mdi:file-document' },
        { id: 'sede', name: 'Sede', icon: 'mdi:store' },
        { id: 'tecnico', name: 'Técnico', icon: 'mdi:account-wrench' }
      ],
      activeStep: 'empresa',

      form: { tenantId: '', nombreEmpresa: '' },
      formConfig: {
        logoBase64: '',
        faviconBase64: '',
        nombreComercial: '',
        rif: '',
        direccion: '',
        ciudad: '',
        telefono1: '',
        telefono2: '',
        email: '',
        slogan: '',
        whatsapp: '',
        instagram: '',
        facebook: '',
        tiktok: ''
      },
      formSede: {
        nombre: '',
        direccion: '',
        telefono: '',
        email: '',
        ciudad: ''
      },
      formTecnico: {
        nombre: '',
        apellido: '',
        especialidad: '',
        telefono: '',
        comisionPorcentaje: 0,
        descripcion: ''
      },
      logoPreview: '',
      faviconPreview: ''
    };
  },
  computed: {
    filteredEmpresas() {
      let result = this.empresas;
      if (this.searchQuery) {
        const query = this.searchQuery.toLowerCase();
        result = result.filter(e => 
          e.nombre?.toLowerCase().includes(query) || 
          e.id?.toLowerCase().includes(query) ||
          e.databaseName?.toLowerCase().includes(query)
        );
      }
      if (this.filtroEstado !== 'todos') {
        const isActive = this.filtroEstado === 'activo';
        result = result.filter(e => e.activo === isActive);
      }
      return result;
    },
    // 👈 Al editar solo mostramos 2 pasos (Empresa y Info)
    stepsFiltrados() {
      if (this.isEditing) {
        return this.steps.filter(s => s.id === 'empresa' || s.id === 'info');
      }
      return this.steps;
    },
    isSuperAdmin() {
      const user = getCurrentUser();
      return user?.idRol === 1;
    },
    canCreate() {
      return this.isSuperAdmin;
    }
  },
  mounted() {
    this.fetchData();
  },
  beforeDestroy() {
    if (this.toastTimeout) clearTimeout(this.toastTimeout);
  },
  methods: {
    showToast(message, type = 'success') {
      if (this.toastTimeout) clearTimeout(this.toastTimeout);
      this.toastMessage = message;
      this.toastType = type;
      this.toastTimeout = setTimeout(() => { this.toastMessage = ''; }, 4000);
    },

    // 👈 Identifica si es la empresa principal (no se puede eliminar/desactivar)
    esEmpresaPrincipal(empresa) {
      if (!empresa) return false;
      const id = (empresa.id || '').toLowerCase();
      const db = (empresa.databaseName || '').toLowerCase();
      return id === TENANT_PRINCIPAL || 
             id === '' || 
             db === DB_PRINCIPAL ||
             db === 'jeaninformatico_db' ||
             db === 'jeaninformatico';
    },

    // 👈 NAVEGACIÓN DE PASOS (usa stepsFiltrados)
    nextStep() {
      const idx = this.stepsFiltrados.findIndex(s => s.id === this.activeStep);
      if (idx < this.stepsFiltrados.length - 1) {
        if (this.activeStep === 'empresa') {
          if (!this.form.nombreEmpresa.trim()) {
            this.showToast('El nombre de la empresa es obligatorio', 'error');
            return;
          }
          if (!this.form.tenantId.trim()) {
            this.showToast('El Tenant ID es obligatorio', 'error');
            return;
          }
        }
        this.activeStep = this.stepsFiltrados[idx + 1].id;
      }
    },

    prevStep() {
      const idx = this.stepsFiltrados.findIndex(s => s.id === this.activeStep);
      if (idx > 0) {
        this.activeStep = this.stepsFiltrados[idx - 1].id;
      }
    },

    toTitleCase(str) {
      if (!str) return '';
      return str.toLowerCase().split(' ').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ');
    },

    generarTenantId() {
      if (!this.form.nombreEmpresa) { this.form.tenantId = ''; return; }
      let tenantId = this.form.nombreEmpresa
        .normalize('NFD').replace(/[\u0300-\u036f]/g, '')
        .toLowerCase().trim()
        .replace(/[^a-z0-9\s\-_]/g, '')
        .replace(/\s+/g, '_').replace(/_+/g, '_')
        .replace(/^-+|-+$/g, '');
      if (tenantId.length > 50) tenantId = tenantId.substring(0, 50);
      this.form.tenantId = tenantId;
    },

    regenerarTenantId() {
      this.generarTenantId();
      if (this.form.tenantId) this.showToast('Tenant ID regenerado', 'success');
    },

    copiarTenantId() {
      if (!this.form.tenantId) { this.showToast('No hay ID para copiar', 'error'); return; }
      navigator.clipboard.writeText(this.form.tenantId).then(() => this.showToast('Copiado', 'success'));
    },

    async onLogoSelected(event) {
      const file = event.target.files[0];
      if (!file) return;
      if (file.size > 2 * 1024 * 1024) { this.showToast('El logo no debe superar 2MB', 'error'); return; }
      try {
        const base64 = await archivoABase64(file);
        this.formConfig.logoBase64 = base64;
        this.logoPreview = base64;
      } catch (e) { this.showToast('Error al procesar el logo', 'error'); }
    },

    async onFaviconSelected(event) {
      const file = event.target.files[0];
      if (!file) return;
      if (file.size > 500 * 1024) { this.showToast('El favicon no debe superar 500KB', 'error'); return; }
      try {
        const base64 = await archivoABase64(file);
        this.formConfig.faviconBase64 = base64;
        this.faviconPreview = base64;
      } catch (e) { this.showToast('Error al procesar el favicon', 'error'); }
    },

    quitarLogo() { this.formConfig.logoBase64 = ''; this.logoPreview = ''; },
    quitarFavicon() { this.formConfig.faviconBase64 = ''; this.faviconPreview = ''; },

    formatDate(dateString) {
      if (!dateString) return 'N/A';
      try {
        return new Date(dateString).toLocaleDateString('es-ES', { year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
      } catch { return dateString; }
    },

    async fetchData() {
      this.loading = true;
      try {
        const res = await obtenerEmpresas();
        if (res.success) this.empresas = res.data;
        else { this.showToast(res.error || "Error al obtener empresas", 'error'); this.empresas = []; }
      } catch (err) { this.showToast("Error crítico de red", 'error'); this.empresas = []; }
      finally { this.loading = false; }
    },

    // ============================================================
    // CREAR
    // ============================================================
    openCreateModal() {
      if (!this.canCreate) { this.showToast('No tienes permisos para crear empresas', 'error'); return; }
      this.isEditing = false;
      this.empresaEditando = null;
      this.form = { tenantId: '', nombreEmpresa: '' };
      this.formConfig = { logoBase64: '', faviconBase64: '', nombreComercial: '', rif: '', direccion: '', ciudad: '', telefono1: '', telefono2: '', email: '', slogan: '', whatsapp: '', instagram: '', facebook: '', tiktok: '' };
      this.formSede = { nombre: '', direccion: '', telefono: '', email: '', ciudad: '' };
      this.formTecnico = { nombre: '', apellido: '', especialidad: '', telefono: '', comisionPorcentaje: 0, descripcion: '' };
      this.logoPreview = '';
      this.faviconPreview = '';
      this.activeStep = 'empresa';
      this.showModal = true;
      this.$nextTick(() => { if (this.$refs.nombreInput) this.$refs.nombreInput.focus(); });
    },

    // ============================================================
    // EDITAR
    // ============================================================
    async openEditModal(empresa) {
      if (!this.isSuperAdmin) { 
        this.showToast('Solo el Super Admin puede editar empresas', 'error'); 
        return; 
      }

      this.isEditing = true;
      this.empresaEditando = empresa;
      this.form = { 
        tenantId: empresa.id, 
        nombreEmpresa: empresa.nombre || '' 
      };
      
      // Reset defaults
      this.formConfig = { 
        logoBase64: '', faviconBase64: '', nombreComercial: '', rif: '', direccion: '', ciudad: '', 
        telefono1: '', telefono2: '', email: '', slogan: '', whatsapp: '', instagram: '', facebook: '', tiktok: '' 
      };
      this.formSede = { nombre: '', direccion: '', telefono: '', email: '', ciudad: '' };
      this.formTecnico = { nombre: '', apellido: '', especialidad: '', telefono: '', comisionPorcentaje: 0, descripcion: '' };
      this.logoPreview = '';
      this.faviconPreview = '';
      this.activeStep = 'empresa';
      this.showModal = true;

      // Cargar config desde el backend
      try {
        const res = await obtenerConfigEmpresa(empresa.id);
        if (res.success && res.data) {
          const cfg = res.data;
          this.formConfig = {
            logoBase64: cfg.logoBase64 || '',
            faviconBase64: cfg.faviconBase64 || '',
            nombreComercial: cfg.nombreComercial || '',
            rif: cfg.rif || '',
            direccion: cfg.direccion || '',
            ciudad: cfg.ciudad || '',
            telefono1: cfg.telefono1 || '',
            telefono2: cfg.telefono2 || '',
            email: cfg.email || '',
            slogan: cfg.slogan || '',
            whatsapp: cfg.whatsapp || '',
            instagram: cfg.instagram || '',
            facebook: cfg.facebook || '',
            tiktok: cfg.tiktok || ''
          };

          // Previews: logo puede venir como base64 o como logoUrl
          if (cfg.logoBase64) {
            this.logoPreview = cfg.logoBase64;
          } else if (cfg.logoUrl) {
            this.logoPreview = cfg.logoUrl;   // el backend debe servir la URL completa o relativa
          }

          if (cfg.faviconBase64) {
            this.faviconPreview = cfg.faviconBase64;
          } else if (cfg.faviconUrl) {
            this.faviconPreview = cfg.faviconUrl;
          }
        }
      } catch (e) {
        console.warn('No se pudo cargar la config de la empresa:', e);
        this.showToast('No se pudo cargar la configuración visual', 'error');
      }
    },

    closeModal() { 
      this.showModal = false; 
      this.isEditing = false;
      this.empresaEditando = null;
    },

    // ============================================================
    // GUARDAR (crear o editar)
    // ============================================================
    async saveForm() {
      if (!this.form.nombreEmpresa.trim()) { this.showToast('El nombre es obligatorio', 'error'); return; }
      if (!this.form.tenantId.trim()) { this.showToast('El Tenant ID es obligatorio', 'error'); return; }

      this.saving = true;
      try {
        if (this.isEditing) {
          await this.guardarEdicion();
        } else {
          await this.crearNuevaEmpresa();
        }
      } catch (e) {
        console.error('Error al guardar:', e);
        this.showToast("Error al guardar la empresa", 'error');
      } finally { 
        this.saving = false; 
      }
    },

    // ============================================================
    // CREAR EMPRESA NUEVA
    // ============================================================
    async crearNuevaEmpresa() {
      const dataToSend = {
        tenantId: this.form.tenantId.trim().toLowerCase(),
        nombreEmpresa: this.form.nombreEmpresa.trim()
      };

      const res = await crearEmpresa(dataToSend);
      
      if (!res.success) {
        this.showToast(res.error || "Error al crear la empresa", 'error');
        return;
      }

      // Config visual
      const configTenant = {
        idEmpresa: dataToSend.tenantId,
        nombreEmpresa: this.form.nombreEmpresa.trim(),
        nombreComercial: this.formConfig.nombreComercial || this.form.nombreEmpresa.trim(),
        rif: this.formConfig.rif,
        direccion: this.formConfig.direccion,
        ciudad: this.formConfig.ciudad,
        telefono1: this.formConfig.telefono1,
        telefono2: this.formConfig.telefono2,
        email: this.formConfig.email,
        slogan: this.formConfig.slogan,
        whatsapp: this.formConfig.whatsapp,
        instagram: this.formConfig.instagram,
        facebook: this.formConfig.facebook,
        tiktok: this.formConfig.tiktok
      };

      // 👈 CLAVE: guardar SIN aplicar al DOM (no cambia el logo del super admin)
      await guardarConfigTenantSinAplicar(
        dataToSend.tenantId,
        configTenant,
        this.formConfig.logoBase64,
        this.formConfig.faviconBase64 || this.formConfig.logoBase64
      );

      // Crear sede
      if (this.formSede.nombre && this.formSede.nombre.trim()) {
        try {
          const sedeData = {
            nombre: this.formSede.nombre.trim(),
            direccion: this.formSede.direccion?.trim() || configTenant.direccion,
            telefono: this.formSede.telefono?.trim() || configTenant.telefono1,
            email: this.formSede.email?.trim() || configTenant.email,
            ciudad: this.formSede.ciudad?.trim() || configTenant.ciudad,
            estado: 'activo'
          };
          await crearSede(sedeData);
        } catch (sedeError) {
          console.warn('⚠️ No se pudo crear la sede:', sedeError);
        }
      }

      // Crear técnico
      if (this.formTecnico.nombre && this.formTecnico.nombre.trim()) {
        try {
          const tecnicoData = {
            nombre: this.formTecnico.nombre.trim(),
            apellido: this.formTecnico.apellido?.trim() || '',
            especialidad: this.formTecnico.especialidad?.trim() || '',
            telefono: this.formTecnico.telefono?.trim() || '',
            comisionPorcentaje: this.formTecnico.comisionPorcentaje || 0,
            descripcion: this.formTecnico.descripcion?.trim() || '',
            estado: 'activo'
          };
          await crearTecnico(tecnicoData);
        } catch (tecnicoError) {
          console.warn('⚠️ No se pudo crear el técnico:', tecnicoError);
        }
      }

      this.showToast('Empresa creada exitosamente', 'success');
      this.closeModal();
      this.fetchData();
    },

    // ============================================================
    // GUARDAR EDICIÓN
    // ============================================================
    async guardarEdicion() {
      const tenantId = this.form.tenantId.trim().toLowerCase();
      const empresaOriginal = this.empresaEditando;
      
      if (!empresaOriginal) {
        this.showToast('No se encontró la empresa a editar', 'error');
        return;
      }

      const configTenant = {
        idEmpresa: tenantId,
        nombreEmpresa: empresaOriginal.nombre,   // no cambia
        nombreComercial: this.formConfig.nombreComercial || empresaOriginal.nombre,
        rif: this.formConfig.rif,
        direccion: this.formConfig.direccion,
        ciudad: this.formConfig.ciudad,
        telefono1: this.formConfig.telefono1,
        telefono2: this.formConfig.telefono2,
        email: this.formConfig.email,
        slogan: this.formConfig.slogan,
        whatsapp: this.formConfig.whatsapp,
        instagram: this.formConfig.instagram,
        facebook: this.formConfig.facebook,
        tiktok: this.formConfig.tiktok
      };

      // 👈 CLAVE: ¿Estoy editando MI propia empresa actual?
      // Solo en ese caso aplicamos al DOM para que veas los cambios en vivo.
      const configActual = obtenerConfigTenant();
      const esMiEmpresa = configActual?.idEmpresa === tenantId;

      let ok = false;
      if (esMiEmpresa) {
        ok = await guardarConfigTenant(
          tenantId,
          configTenant,
          this.formConfig.logoBase64,
          this.formConfig.faviconBase64 || this.formConfig.logoBase64
        );
      } else {
        ok = await guardarConfigTenantSinAplicar(
          tenantId,
          configTenant,
          this.formConfig.logoBase64,
          this.formConfig.faviconBase64 || this.formConfig.logoBase64
        );
      }

      if (ok) {
        this.showToast('Empresa actualizada correctamente', 'success');
        this.closeModal();
        this.fetchData();
      } else {
        this.showToast('No se pudieron guardar los cambios', 'error');
      }
    },

    async toggleStatus(empresa) {
      // 👈 PROTECCIÓN: no permitir desactivar la empresa principal
      if (this.esEmpresaPrincipal(empresa)) {
        this.showToast('La empresa principal no se puede desactivar', 'error');
        return;
      }

      const nuevoEstado = !empresa.activo;
      const estadoTexto = nuevoEstado ? 'activar' : 'desactivar';
      if (!confirm(`¿${estadoTexto.charAt(0).toUpperCase() + estadoTexto.slice(1)} la empresa "${empresa.nombre}"?`)) return;
      try {
        const res = await cambiarEstadoEmpresa(empresa.id, nuevoEstado);
        if (res.success) { this.showToast(`Empresa ${estadoTexto}da`, 'success'); this.fetchData(); }
        else this.showToast(res.error || "No se pudo cambiar el estado", 'error');
      } catch (e) { this.showToast("Error al cambiar estado", 'error'); }
    },

    confirmarEliminarConfig(empresa) {
      if (!confirm(`¿Eliminar la configuración visual (logo + datos) de "${empresa.nombre}"?\n\nLa empresa seguirá existiendo en el sistema, solo se borrará el logo y los datos visuales del proyecto.`)) return;
      
      const tenantId = empresa.id || empresa.tenantId;
      eliminarConfigTenant(tenantId).then(res => {
        if (res) { this.showToast('Configuración visual eliminada', 'success'); this.fetchData(); }
        else this.showToast('Error al eliminar la configuración', 'error');
      });
    },

    confirmDelete(empresa) {
      if (!this.isSuperAdmin) { this.showToast('Solo el Super Admin puede eliminar', 'error'); return; }
      // 👈 PROTECCIÓN: no permitir eliminar la empresa principal
      if (this.esEmpresaPrincipal(empresa)) {
        this.showToast('La empresa principal no se puede eliminar', 'error');
        return;
      }
      this.empresaAEliminar = empresa;
      this.showDeleteModal = true;
    },

    closeDeleteModal() { this.showDeleteModal = false; this.empresaAEliminar = null; },

    async deleteEmpresa() {
      if (!this.empresaAEliminar) return;
      // 👈 doble verificación
      if (this.esEmpresaPrincipal(this.empresaAEliminar)) {
        this.showToast('La empresa principal no se puede eliminar', 'error');
        this.closeDeleteModal();
        return;
      }
      this.deleting = true;
      try {
        const res = await eliminarEmpresa(this.empresaAEliminar.id);
        if (res.success) {
          this.showToast(`Empresa eliminada`, 'success');
          this.closeDeleteModal();
          this.fetchData();
        } else this.showToast(res.error || "No se pudo eliminar", 'error');
      } catch (e) { this.showToast("Error al eliminar", 'error'); }
      finally { this.deleting = false; }
    }
  }
};
</script>

<style scoped>
@keyframes fadeIn { from { opacity: 0; transform: translateY(-10px); } to { opacity: 1; transform: translateY(0); } }
@keyframes slideUp { from { opacity: 0; transform: translateY(20px); } to { opacity: 1; transform: translateY(0); } }
.animate-fadeIn { animation: fadeIn 0.3s ease-out; }
.animate-slideUp { animation: slideUp 0.3s ease-out; }
.custom-scrollbar::-webkit-scrollbar { height: 4px; width: 6px; }
.custom-scrollbar::-webkit-scrollbar-track { background: transparent; }
.custom-scrollbar::-webkit-scrollbar-thumb { background: #cbd5e0; border-radius: 4px; }
.dark .custom-scrollbar::-webkit-scrollbar-thumb { background: #4b5563; }
.overflow-x-auto::-webkit-scrollbar { height: 4px; }
.overflow-x-auto::-webkit-scrollbar-track { background: #f1f1f1; border-radius: 4px; }
.overflow-x-auto::-webkit-scrollbar-thumb { background: #cbd5e0; border-radius: 4px; }
.dark .overflow-x-auto::-webkit-scrollbar-track { background: #1f2937; }
.dark .overflow-x-auto::-webkit-scrollbar-thumb { background: #4b5563; }
</style>